import { userInfoStore } from '../../../store/userInfoStore';
import { checkHashStore } from '../../../store/checkHashStore';
import { userSettingsStore } from '../../../store/userSettingsStore';
import {
  BlackJackGameMode,
  BlackJackHandNameByType,
  CARD_OWNER,
  DARK_CARD_STATUS,
  DECISION_RESULT_EVENTS,
  DECISION_TYPE,
  INSURANCE_ACTION_TYPE,
  SOCKET_INVOKE_TARGETS,
} from '../blackJackCore/constants';
import { blackJackGameConnection } from '../blackJackConnection';
import { ValidationType } from '../../../core/constants/validationType';
import { blackJackBetsStore } from '../blackJackStore/betStore';
import { betHistoryStore } from '../../../store/betHistoryStore';
import { TranslationKeys } from '../../../core/constants/translationKeys';
import { notificationStore } from '../../../store/notificationStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { BlackJackSoundsName } from '../blackJackCore/constants/sounds';
import { mainGameConnection } from '../../../core/mainGameConnection/mainGameConnection';
import { iframeService } from '../../../services/iframeService';
import { freeBetStore } from '../../../store/freeBetStore';

class BlackJackGameService {
  onGetInitialStateResult({
    maxWinLimit,
    possibleSpinActionInfo,
    spinInitialInfo,
    dealerCardInfo,
    lastSpinInfo,
  }) {
    if (lastSpinInfo) {
      checkHashStore.setLastRoundInfo(lastSpinInfo);
      checkHashStore.setLastRoundResult({
        openedCards: lastSpinInfo.openedCards,
        id: lastSpinInfo.id,
      });
    }
    blackJackBetsStore.setMaxWinLimit(maxWinLimit);
    if (possibleSpinActionInfo) {
      const { handType, spinId, possibleActions } = possibleSpinActionInfo;
      blackJackBetsStore.setPossibleActions(possibleActions || []);
      blackJackBetsStore.setCurrentSpinId(spinId);
      blackJackBetsStore.setActiveHandType(handType);
      blackJackBetsStore.setIsRoundStarted(Boolean(possibleActions?.length));
      if (possibleActions?.includes(INSURANCE_ACTION_TYPE.YES)) {
        if (freeBetStore.currentFreeBet) {
          const model = {
            isInsurance: false,
            spinId: blackJackBetsStore.currentSpinId,
            mode: userSettingsStore.isTurboOn ? BlackJackGameMode.Turbo : BlackJackGameMode.Normal,
          };
          blackJackGameConnection.sendInsurance(model);
        } else {
          blackJackBetsStore.setIsShowInsurance(true);
        }
      } else {
        blackJackBetsStore.setIsShowDecision(Boolean(possibleActions?.length));
      }
    }
    if (spinInitialInfo) {
      const { spinsInfo } = spinInitialInfo;
      spinsInfo
        .filter((el) => el.mainSpinCards || el.splitSpinCards)
        .forEach((el) => {
          const {
            mainSpin,
            mainSpinCards,
            totalPoint,
            secondPoint,
            lastAction,
            hasBlackjack,
            handType,
            splitSpin,
            splitSpinCards,
          } = el;
          blackJackBetsStore.setUserCardsInfo({
            ...blackJackBetsStore.userCardsInfo,
            [BlackJackHandNameByType[handType]]: {
              cards: mainSpinCards || splitSpinCards,
              totalPoint,
              lastAction,
              hasBlackjack,
              secondPoint,
            },
          });
          if (mainSpin) {
            blackJackBetsStore.setUserMainBet(mainSpin || {});
            userInfoStore.setBetAmount(mainSpin.amount);
          }
          if (splitSpin) {
            blackJackBetsStore.setUserSplitBet(splitSpin || {});
          }
          blackJackBetsStore.setIsDoSpinStart(true);
        });
    }
    if (dealerCardInfo) {
      const { dealerCards, dealerPoint } = dealerCardInfo;
      blackJackBetsStore.setDealerCardsInfo({ cards: dealerCards, totalPoint: dealerPoint });
    }
    checkHashStore.setNextHashTextInfo(TranslationKeys.NextCards);
  }

  doSpin() {
    if (blackJackBetsStore.isDoSpinStart) {
      return;
    }
    blackJackBetsStore.setIsDoSpinStart(true);
    soundService.play(BlackJackSoundsName.bet);
    const model = {
      amount: userInfoStore.betAmount,
      mode: userSettingsStore.isTurboOn ? BlackJackGameMode.Turbo : BlackJackGameMode.Normal,
    };
    blackJackBetsStore.setIsBetsClosed(true);
    mainGameConnection.doSpin(model);
  }

  handleDoSpinResult({ responseCode, balance, freebetInfo, ...mainBet }) {
    blackJackBetsStore.setIsBetsClosed(false);
    if (responseCode === ValidationType.Success) {
      if (freebetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(freebetInfo);
      }
      if (!freebetInfo) {
        userInfoStore.setUserBalance(balance);
      }
      blackJackBetsStore.setIsDoSpinStart(true);
      blackJackBetsStore.resetCardsInfo();
      blackJackBetsStore.setUserMainBet(mainBet);
    } else {
      blackJackBetsStore.setIsDoSpinStart(false);
      notificationStore.setNotification(responseCode);
    }
  }

  handleSpinResult({ key, salt, hash, newHash, spins, freebetInfo, openedCards, mainSpinId }) {
    checkHashStore.setNextRoundHash(newHash);
    checkHashStore.setLastRoundInfo({ key, salt, hash });
    checkHashStore.setLastRoundResult({ openedCards, id: mainSpinId });
    blackJackBetsStore.setIsRoundFinished(true);
    if (freebetInfo !== null) {
      freeBetStore.updateCurrentFreeBet(freebetInfo);
    }
    const mainBet = spins.find(
      (el) =>
        el.id === blackJackBetsStore.userMainBet.id ||
        el.id === blackJackBetsStore.userMainBet.spinId
    );
    blackJackBetsStore.setUserMainBet(mainBet || {});
    const splitBet = spins.find(
      (el) =>
        el.id === blackJackBetsStore.userSplitBet.id ||
        el.id === blackJackBetsStore.userSplitBet.spinId
    );
    blackJackBetsStore.setUserSplitBet(splitBet || {});
    const winAmount = spins.reduce((acc, current) => acc + current.winAmount, 0);
    const totalAmount = spins.reduce((acc, current) => acc + current.amount, 0);
    const odd = spins.reduce((acc, current) => acc + current.odd, 0);
    setTimeout(() => {
      soundService.play(winAmount ? BlackJackSoundsName.win : BlackJackSoundsName.lose);
      blackJackBetsStore.setWinAmount(winAmount);
      blackJackBetsStore.setWinOdd(odd);
      iframeService.sendRoundFinished(winAmount, totalAmount);
    }, 500);

    setTimeout(() => {
      blackJackBetsStore.resetStore();
      betHistoryStore.getLastBet(spins.length);
      userInfoStore.getBalance();
    }, 1500);
  }

  sendInsurance(isInsurance) {
    if (blackJackBetsStore.isBlockSendDecision) {
      return;
    }
    const model = {
      isInsurance,
      spinId: blackJackBetsStore.currentSpinId,
      mode: userSettingsStore.isTurboOn ? BlackJackGameMode.Turbo : BlackJackGameMode.Normal,
    };
    blackJackBetsStore.setIsBlockSendDecision(true);
    blackJackGameConnection.sendInsurance(model);
  }

  sendDecisionAction(type) {
    if (blackJackBetsStore.isBlockSendDecision) {
      return;
    }
    soundService.play(BlackJackSoundsName.decision);
    const model = {
      spinId: blackJackBetsStore.currentSpinId,
      mode: userSettingsStore.isTurboOn ? BlackJackGameMode.Turbo : BlackJackGameMode.Normal,
    };
    blackJackBetsStore.setIsBlockSendDecision(true);
    blackJackGameConnection.sendDecisionAction(SOCKET_INVOKE_TARGETS[type], model);
  }

  handleDecisionEvents(eventName, data) {
    if (data.operatorError) {
      notificationStore.setOperatorError(data.operatorError);
    }
    if (data.responseCode === ValidationType.Success) {
      this.combineDecisionEvents[eventName](data);
      blackJackBetsStore.setActiveHandType(null);
      blackJackBetsStore.setIsShowDecision(false);
    } else {
      blackJackBetsStore.setIsBlockSendDecision(false);
      notificationStore.setNotification(data.responseCode);
    }
  }

  handleActionResult({ decision, totalPoint, secondPoint, openedCard, isDouble, hasBlackJack }) {
    this.handleAnimation(openedCard);
    blackJackBetsStore.updateCards({
      totalPoint,
      openedCard,
      isDouble,
      secondPoint,
      decision,
      hasBlackJack,
    });
  }

  dealingCard({ openedCardsInfo, isDouble = false, decision = null }) {
    let time = 500;
    openedCardsInfo.forEach(({ openedCard, totalPoint, secondPoint, hasBlackJack }) => {
      setTimeout(() => {
        soundService.play(BlackJackSoundsName.card);
        this.handleActionResult({
          isDouble,
          decision,
          totalPoint,
          secondPoint,
          openedCard,
          hasBlackJack,
        });
      }, time);
      time += 500;
    });
  }

  handleHitResult({ cardInfo, freebetInfo }) {
    this.dealingCard({ openedCardsInfo: cardInfo, decision: DECISION_TYPE.hit });
    if (freebetInfo !== null) {
      freeBetStore.updateCurrentFreeBet(freebetInfo);
    }
  }

  handleStandResult() {
    blackJackBetsStore.setActiveHandType(null);
  }

  handleSplitResult({ cardInfo, balance, freebetInfo }) {
    if (freebetInfo !== null) {
      freeBetStore.updateCurrentFreeBet(freebetInfo);
    }
    if (!freebetInfo) {
      userInfoStore.setUserBalance(balance);
    }
    blackJackBetsStore.splitUserCards(cardInfo);
    this.dealingCard({ openedCardsInfo: cardInfo, decision: DECISION_TYPE.split });
  }

  handleDoubleResult({ cardInfo, freebetInfo, balance }) {
    if (!freebetInfo) {
      userInfoStore.setUserBalance(balance);
    }
    this.dealingCard({
      openedCardsInfo: cardInfo,
      isDouble: true,
      decision: DECISION_TYPE.double,
    });
  }

  handleInsuranceResult({ responseCode, balance, isInsurance }) {
    if (responseCode === ValidationType.Success) {
      if (isInsurance) {
        userInfoStore.setUserBalance(balance);
      }
      blackJackBetsStore.setIsShowInsurance(false);
    }
  }

  handleDecisionResult({ possibleActions, spinId, handType }) {
    blackJackBetsStore.setIsRoundStarted(true);
    blackJackBetsStore.setIsBlockSendDecision(false);
    blackJackBetsStore.setCurrentSpinId(spinId);
    blackJackBetsStore.setPossibleActions(possibleActions);
    blackJackBetsStore.setActiveHandType(handType);
    if (possibleActions.includes(INSURANCE_ACTION_TYPE.YES)) {
      if (freeBetStore.currentFreeBet) {
        const model = {
          isInsurance: false,
          spinId: blackJackBetsStore.currentSpinId,
          mode: userSettingsStore.isTurboOn ? BlackJackGameMode.Turbo : BlackJackGameMode.Normal,
        };
        blackJackBetsStore.setIsShowInsurance(false);
        blackJackGameConnection.sendInsurance(model);
      } else {
        blackJackBetsStore.setIsShowInsurance(true);
      }
    } else {
      blackJackBetsStore.setIsShowDecision(true);
    }
  }

  handleCardResult(data) {
    this.dealingCard({ openedCardsInfo: [data] });
  }

  handleAnimation(openedCard) {
    const lastDealerCard =
      blackJackBetsStore.dealerCardsInfo.cards[blackJackBetsStore.dealerCardsInfo.cards.length - 1];
    if (openedCard.owner === CARD_OWNER.Dealer && lastDealerCard?.status === DARK_CARD_STATUS) {
      return;
    }
    blackJackBetsStore.setIsStartAnimation(true);
    blackJackBetsStore.setStartedCardAnimationIndex(openedCard.index);
    setTimeout(() => {
      blackJackBetsStore.setIsStartAnimation(false);
      blackJackBetsStore.setStartedCardAnimationIndex(null);
    }, 150);
  }

  combineDecisionEvents = {
    [DECISION_RESULT_EVENTS.HitResult]: this.handleHitResult.bind(this),
    [DECISION_RESULT_EVENTS.StandResult]: this.handleStandResult.bind(this),
    [DECISION_RESULT_EVENTS.SplitResult]: this.handleSplitResult.bind(this),
    [DECISION_RESULT_EVENTS.DoubleResult]: this.handleDoubleResult.bind(this),
    [DECISION_RESULT_EVENTS.InsuranceResult]: this.handleInsuranceResult.bind(this),
  };
}

export const blackJackGameService = new BlackJackGameService();
